import type { Experiment } from 'types';
import { Fragment, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatDateTime, formatDate, daysAgo } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { userFullName } from 'helpers/user-identity';
import { UserAvatar } from '@/components/UserAvatar';
import { isPreStart } from 'helpers/experiment-status';

type TimelineProps = { experiment: Experiment };

type StepTone = { dot: string; label: string };

/**
 * The track's pieces are exported for the feature flag page's status card, which draws its
 * draft, live and archived stages on the same track (draft, running and ended tones).
 *
 * Colour carries the stage, never decoration: draft is a selection/draft state, running
 * is live, deployed is the shipped one. Ended is complete rather than broken, so it stays
 * neutral — `danger` is reserved for something actually wrong.
 */
export const STEP_TONE: Record<string, StepTone> = {
  draft: { dot: 'bg-bar-muted', label: 'text-dim' },
  running: { dot: 'bg-ok', label: 'text-ok' },
  deployed: { dot: 'bg-accent', label: 'text-accent' },
  ended: { dot: 'bg-bar-muted', label: 'text-foreground' },
};

export const INACTIVE_TONE: StepTone = { dot: 'bg-track', label: 'text-dim' };

export const toneFor = (key: string, active: boolean): StepTone =>
  active ? (STEP_TONE[key] ?? INACTIVE_TONE) : INACTIVE_TONE;

type DotProps = { tone: StepTone };
type StepProps = { stepKey: string; label: string; date?: string; active: boolean };
type LineProps = { stepKey: string; active: boolean };

export const Dot = ({ tone }: DotProps) => <span className={cn('size-[5px] shrink-0 rounded-pill', tone.dot)} />;

export const StepLabel = ({ tone, label }: { tone: StepTone; label: string }) => (
  <span className={cn('font-mono text-nano tracking-[0.1em] lowercase', tone.label)}>{label}</span>
);

export const Step = ({ stepKey, label, date, active }: StepProps) => {
  const tone = toneFor(stepKey, active);
  return (
    <div className="flex items-center gap-2">
      <Dot tone={tone} />
      <div className="flex flex-col gap-0.5">
        <StepLabel tone={tone} label={label} />
        {date ? <span className="log-line text-dim">{date}</span> : null}
      </div>
    </div>
  );
};

/**
 * 2px meter segment: the track colour until the next stage is reached.
 *
 * `flex-1`, not a fixed width. At `w-20` the four stages bunched into the left
 * 40% of the panel and left a void across the rest — the track is a progress
 * meter, so it should span what it is measuring.
 */
export const Line = ({ stepKey, active }: LineProps) => (
  <div className={cn('bg-track h-0.5 min-w-8 flex-1 rounded-pill', active && toneFor(stepKey, true).dot)} />
);

export const Timeline = ({ experiment }: TimelineProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const createdAt = formatDate(experiment.createdAt || Date.now());
  const endsAt = formatDateTime(experiment.endsAt || 0);

  const deployedVariant = experiment.variants.find((v: any) => v.deployed);

  const startsAtDate = experiment.startsAt ? formatDate(experiment.startsAt) : undefined;
  const deployedAtDate = deployedVariant?.deployed ? formatDate(deployedVariant.deployed) : undefined;

  const status = (experiment.status || '').toString();

  const isDraft = isPreStart(status);
  const isQa = status === 'QA';
  const isRunning = status === 'Running';
  const isDeployed = status === 'Deployed';
  const isEnded = status === 'Ended';

  const hasStarted = !isDraft;
  const hasDeployed = isDeployed || !!deployedVariant;
  const hasEnded = isEnded;

  const statusKey = isEnded ? 'ended' : isDeployed ? 'deployed' : isRunning ? 'running' : 'draft';
  const statusTone = STEP_TONE[statusKey] ?? INACTIVE_TONE;

  const statusLabel = isEnded ? 'Ended' : isDeployed ? 'Deployed' : isRunning ? 'Running' : isQa ? 'QA' : 'Draft';

  const runStartTs = experiment.startsAt || null;

  const runEndTs = hasEnded
    ? experiment.endsAt || null
    : isRunning
      ? Date.now()
      : isDeployed
        ? deployedVariant?.deployed || experiment.endsAt || Date.now()
        : null;

  const runDays = runStartTs && runEndTs ? daysAgo(runStartTs, runEndTs) : null;
  const hasRunWindow = !!runStartTs && !!runEndTs;

  const showShortRunNotice = !!runDays && runDays > 0 && runDays < 14;

  /**
   * The dates only, with no status word.
   *
   * It used to read "Running: 8/14 – 8/17 • 4 days" while the chip beside it
   * already said "running", so the word was printed twice on one line.
   */
  const runWindowText =
    runStartTs && runEndTs
      ? `${formatDate(runStartTs)} – ${formatDate(runEndTs)}${runDays ? ` · ${runDays} days` : ''}`
      : null;

  const steps = [
    { key: 'draft', label: 'Draft', date: createdAt, active: true },
    { key: 'running', label: 'Running', date: hasStarted ? startsAtDate : undefined, active: hasStarted },
    { key: 'deployed', label: 'Deployed', date: hasDeployed ? deployedAtDate : undefined, active: hasDeployed },
    { key: 'ended', label: 'Ended', date: hasEnded ? endsAt : undefined, active: hasEnded },
  ] as const;

  const { createdByUser: creator } = experiment;

  return (
    <section className="bg-panel border-line rounded-panel border">
      {/* One status flag, not three.
          The strip used to read "• running" while a pill under it read "Running:
          8/14 – 8/17 • 4 days" and the track below said "running" a third time. The
          dated one is the only one that tells you anything you cannot already see,
          so it is the one that survives, and it sits here where the bare chip was.

          Below `md` this strip is the disclosure for the stacked step list, which is
          why it is a button — the steps have nowhere to go on a phone. */}
      <button
        type="button"
        onClick={() => setMobileOpen((open) => !open)}
        aria-expanded={mobileOpen}
        className={cn(
          'border-line bg-panel2 rounded-t-panel flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b px-4 py-2.5 text-left',
          'md:pointer-events-none md:cursor-default',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('size-[5px] shrink-0 rounded-pill', statusTone.dot, isRunning && 'animate-pulse')} />
          <span className={cn('font-mono text-nano tracking-[0.1em] lowercase', statusTone.label)}>
            {statusLabel.toLowerCase()}
          </span>
          {!!runWindowText && (
            <>
              <span className="text-dim font-mono text-nano">·</span>
              {/* The dates wear the status colour, so a running experiment reads green
                  across the whole flag rather than changing tone halfway through it.
                  A run shorter than two weeks rarely covers a full weekly cycle -
                  that used to be carried by turning these amber, which fought the
                  status; it is a title now so the colour keeps meaning one thing. */}
              <span
                className={cn('truncate font-mono text-micro', statusTone.label)}
                title={
                  showShortRunNotice
                    ? `${runWindowText} — under two weeks, so this may not cover a full weekly cycle`
                    : runWindowText
                }
              >
                {runWindowText}
              </span>
            </>
          )}
        </span>

        <span className="flex items-center gap-2">
          {/* Badge and first name rather than the badge alone: this strip is a
              button on a phone, and there is no hover to reveal a name on touch. */}
          {!!creator && (
            <span className="micro-label flex min-w-0 items-center gap-1.5" title={userFullName(creator)}>
              <span className="shrink-0">created by</span>
              <UserAvatar user={creator} size="xs" />
              <span className="truncate">{creator.firstName || userFullName(creator)}</span>
            </span>
          )}
          <ChevronDown className={cn('text-dim size-4 transition-transform md:hidden', mobileOpen && 'rotate-180')} />
        </span>
      </button>

      <div className="flex flex-col gap-4 p-4">
        {/* The run window moved up into the status strip; what is left here is the
            stacked step list the strip discloses on a phone. */}
        <div className="w-full md:hidden">
          <div
            className={cn('overflow-hidden transition-all', mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0')}
          >
            <div className="border-line bg-panel2 rounded-card border p-3">
              <div className="flex flex-col gap-3">
                {steps.map((s) => {
                  const tone = toneFor(s.key, s.active);
                  return (
                    <div key={s.key} className="flex items-start justify-between gap-3">
                      <span className="flex items-center gap-2">
                        <Dot tone={tone} />
                        <StepLabel tone={tone} label={s.label} />
                      </span>
                      <span className="log-line text-dim">{s.date || '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Steps and connectors are siblings in one row so the connectors can take
            the slack: the stages sit at the edges and at even intervals between,
            which is what a progress track is meant to look like. */}
        <div className="hidden min-h-[48px] items-center md:flex">
          <div className="flex w-full items-center gap-3">
            {steps.map((s, index) => {
              const nextStep = steps[index + 1];
              return (
                <Fragment key={s.key}>
                  <Step stepKey={s.key} label={s.label} date={s.date} active={s.active} />
                  {nextStep ? <Line stepKey={nextStep.key} active={nextStep.active} /> : null}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
